const WIDGET_SRC = 'https://infiteq.app/widget/infiteq-form.iife.js'
// The widget otherwise derives its API base from the script origin, which serves the SPA shell, not the API.
const FORM_API_URL = 'https://api.infiteq.app'
// Public tenant key (same one the chat widget uses), not a secret.
const TENANT_KEY = 'iq_DOfGYLqfdlFBTypTFQRNAa6If7UU3ftC'

/** Infiteq CRM form IDs per locale. A missing locale means the form isn't shown there. */
export const LEAD_FORMS = {
  website: { sr: '01a0ef13-639a-705d-9fa1-b2bb2d1ca309', en: '01a0ef1f-1307-7136-9b8b-fffe7399d5c2' } as Partial<Record<string, string>>,
  leadsOffer: { sr: '01a0ef15-f504-71f5-8fa8-1ca6e3bde3e4' } as Partial<Record<string, string>>,
}

type FormRenderOptions = {
  target: HTMLElement
  tenantKey: string
  formId: string
  apiUrl?: string
  accent?: string
  onSuccess?: (detail: { formId: string; leadId: string; submissionId: string }) => void
  onDisqualified?: (detail: { formId: string }) => void
  onError?: (detail: { formId: string; phase: string; status?: number; message?: string }) => void
}

type FormWindow = Window & {
  InfiteqForm?: { render: (opts: FormRenderOptions) => void }
  fbq?: (...args: unknown[]) => void
  gtag?: (...args: unknown[]) => void
}

let loader: Promise<void> | null = null

/** Loads the Infiteq form widget script once and resolves when its API is ready. */
function loadFormWidget(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if ((window as FormWindow).InfiteqForm) return Promise.resolve()
  if (loader) return loader

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('Failed to load Infiteq form widget')))
    script.src = WIDGET_SRC
    script.async = true
    document.head.appendChild(script)
  })

  return loader
}

/** Qualified submissions count as leads for Meta and Google; disqualified ones never do. */
function trackLead() {
  const w = window as FormWindow
  w.fbq?.('track', 'Lead')
  w.gtag?.('event', 'generate_lead')
}

/**
 * Mounts an Infiteq CRM form into `target`. The widget only auto-boots once per page load,
 * so client-side navigation and popups have to render explicitly.
 */
export async function renderForm(
  target: HTMLElement,
  formId: string,
  opts: Pick<FormRenderOptions, 'onSuccess' | 'onDisqualified' | 'accent'> = {},
) {
  await loadFormWidget()
  const api = (window as FormWindow).InfiteqForm
  if (!api) return
  api.render({
    target,
    tenantKey: TENANT_KEY,
    formId,
    apiUrl: FORM_API_URL,
    accent: opts.accent,
    onSuccess: (detail) => {
      trackLead()
      opts.onSuccess?.(detail)
    },
    onDisqualified: opts.onDisqualified,
  })
  tightenPadding(target)
}

/**
 * The widget hard-codes `padding: 24px` as an inline style on its root, inside a shadow root.
 * Only an `!important` rule injected into that shadow root can override it.
 */
function tightenPadding(target: HTMLElement) {
  const root = target.shadowRoot
  if (!root || root.querySelector('style[data-infiteq-padding]')) return
  const style = document.createElement('style')
  style.setAttribute('data-infiteq-padding', '')
  style.textContent = 'div[style*="max-width: 560px"] { padding: 4px !important; }'
  root.appendChild(style)
}
